// src/widgets/ConversationWidget.tsx

import { useState, useRef, useEffect, useCallback } from 'react';
import { FaPlay, FaPause } from "react-icons/fa";
import { RiDownload2Fill } from "react-icons/ri";
import { MarkdownContainer } from '../components/MarkdownContainer';
import { useRealtimeService } from '../contexts/RealtimeServiceContext';
import { useFabricService } from '../contexts/FabricServiceContext';
import { AgentDescription } from '../types/AgentDescription';
import { ResponseInsights } from '../components/ResponseInsights';

interface ConversationWidgetProps {
    selectedConversationFilters: (string | undefined)[];
}

export const ConversationWidget: React.FC<ConversationWidgetProps> = ({
    selectedConversationFilters
}) => {

    const [playingAudio, setPlayingAudio] = useState<string | null>(null);
    const audioReference = useRef<{ [key: string]: HTMLAudioElement }>({});
    const previousItemsRef = useRef<any[]>([]);

    const { items, deleteConversationItem, selectedItem, setSelectedItem } = useRealtimeService();
    const { isFabric, instance, fileOptions, fabricItems } = useFabricService();

    const currentItems = isFabric ? fabricItems : items;

    /**
     * Utility for audio buttons
     */
    const toggleAudio = (id: string) => {
        const audio = audioReference.current[id];
        if (audio) {
            if (playingAudio === id) {
                audio.pause();
                setPlayingAudio(null);
            } else {
                if (playingAudio) {
                    audioReference.current[playingAudio].pause();
                }
                audio.play();
                setPlayingAudio(id);
            }
        }
    };

    const downloadAudio = (url: string, filename: string) => {
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const getNameForRole = useCallback((role: string | undefined) => {
        if (!isFabric || !['user', 'assistant'].includes(role || '')) return;
        const file = fileOptions.find((file) => file.id === instance?.seed_id)
        if (!file) return;
        switch (role) {
            case 'user':
                return AgentDescription(file?.user, role).name;
            case 'assistant':
                return AgentDescription(file!.agent, role).name;
            default:
                return;
        }
    }, [isFabric, instance, fileOptions]);

    const handleItemClick = useCallback((conversationItem: any) => {
        if (selectedItem?.id === conversationItem.id) {
            setSelectedItem(undefined);
        } else {
            setSelectedItem(conversationItem);
        }
    }, [selectedItem, setSelectedItem]);

    /**
     * Auto-scroll the conversation logs
     */
    useEffect(() => {
        const conversationEls = [].slice.call(
            document.body.querySelectorAll('[data-conversation-content]')
        );
        for (const el of conversationEls) {
            const conversationEl = el as HTMLDivElement;
            // [TODO] AUTO-SCROLL Diababled, needs to only auto-scroll when needed
            // conversationEl.scrollTop = conversationEl.scrollHeight;
        }
    }, [currentItems]);

    // Maintain selection when items update
    useEffect(() => {
        if (selectedItem) {
            const currentItemExists = currentItems.some(item => item.id === selectedItem.id);
            if (!currentItemExists) {
                setSelectedItem(undefined);
            }
        }
        previousItemsRef.current = currentItems;
    }, [currentItems, selectedItem, setSelectedItem]);

    useEffect(() => {
        const resizeObserver = new ResizeObserver(entries => {
            for (const entry of entries) {
                const content = entry.target as HTMLElement;
                const wrapper = content.closest('.response-insights-wrapper') as HTMLElement;
                if (wrapper) {
                    wrapper.style.setProperty('--content-height', `${entry.contentRect.height}px`);
                }
            }
        });

        const contents = document.querySelectorAll('.response-insights-content');
        contents.forEach(content => resizeObserver.observe(content));

        return () => {
            resizeObserver.disconnect();
        };
    }, [fabricItems, items]);

    const filteredItems = currentItems.filter(item => {
        if (selectedConversationFilters.includes('tool_calls') && item.formatted.tool) {
            return false;
        }
        if (selectedConversationFilters.includes('tool_responses') && item.type === 'function_call_output') {
            return false;
        }
        return true;
    });

    return (
        <div className="content-block conversation">
            <div className="content-block-body" data-conversation-content>
                {!filteredItems.length && `awaiting conversation...`}
                {filteredItems.map((conversationItem) => {
                    const inProgress = 'status' in conversationItem && conversationItem.status != 'completed' && ['assistant', 'user'].includes(conversationItem.role || '');
                    const isSelected = selectedItem?.id === conversationItem.id || (inProgress && selectedItem?.id === undefined);
                    return (
                        <div className="conversation-item" key={conversationItem.id}>
                            <div className={`speaker ${conversationItem.role || ''}`}>
                                <div>
                                    {(
                                        getNameForRole(conversationItem.role) ||
                                        conversationItem.role ||
                                        conversationItem.type
                                    ).replaceAll('_', ' ')}
                                </div>
                                {/* <div
                                    className="close"
                                    onClick={() =>
                                        deleteConversationItem(conversationItem.id)
                                    }
                                >
                                    <X />
                                </div> */}
                            </div>
                            <div className='speaker-content-stack'>
                                <div className={`speaker-content ${(inProgress ? 'wave-text' : '')}`}>
                                    <div
                                        className={`speaker-content-highlight ${isSelected ? 'selected' : ''}`}
                                        style={{ display: conversationItem.role === 'assistant' ? 'inherit' : 'none' }}
                                        onClick={() => handleItemClick(conversationItem)}
                                    />
                                    {/* tool response */}
                                    {conversationItem.type === 'function_call_output' && (
                                        <MarkdownContainer
                                            content={conversationItem.formatted.output}
                                            type="json"
                                        />
                                    )}
                                    {/* tool call */}
                                    {!!conversationItem.formatted.tool && (
                                        <MarkdownContainer
                                            content={JSON.stringify({
                                                name: conversationItem.formatted.tool.name,
                                                arguments: conversationItem.formatted.tool.arguments
                                            })}
                                            type="function"
                                        />
                                    )}
                                    {!conversationItem.formatted.tool &&
                                        conversationItem.role === 'user' && (
                                            <div>
                                                {conversationItem.formatted.transcript ||
                                                    (conversationItem.formatted.audio?.length
                                                        ? '(awaiting transcript)'
                                                        : conversationItem.formatted.text ||
                                                        '(item sent)')}
                                            </div>
                                        )}
                                    {!conversationItem.formatted.tool &&
                                        conversationItem.role === 'assistant' && (
                                            <div
                                                className='interactive-assistant'
                                                dangerouslySetInnerHTML={{
                                                    __html: (
                                                        conversationItem.formatted.transcript ||
                                                        conversationItem.formatted.text ||
                                                        '(truncated)'
                                                    )
                                                }}
                                            />
                                        )}
                                    {conversationItem.formatted.file && (
                                        <div className="audio-controls">
                                            <audio
                                                ref={(el) => {
                                                    if (el) audioReference.current[conversationItem.id] = el;
                                                }}
                                                src={conversationItem.formatted.file.url}
                                                onEnded={() => toggleAudio(conversationItem.id)}
                                                style={{ display: 'none' }}
                                            />
                                            <button
                                                className="audio-button"
                                                onClick={() => toggleAudio(conversationItem.id)}
                                            >
                                                {playingAudio === conversationItem.id ? <FaPause size={8} /> : <FaPlay size={8} />}
                                            </button>
                                            <button
                                                className="audio-button"
                                                onClick={() => downloadAudio(conversationItem.formatted.file.url, `audio_${conversationItem.id}.wav`)}
                                            >
                                                <RiDownload2Fill size={12} />
                                            </button>
                                        </div>
                                    )}
                                </div>
                                {conversationItem.id.includes('resp_') && (
                                    <div className={`response-insights-wrapper ${(isSelected || inProgress) ? 'visible' : ''}`}>
                                        <div className="response-insights-content">
                                            <ResponseInsights id={conversationItem.id} inProgress={inProgress} />
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};